import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@hostesa/ui';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useHotelContext } from '../lib/hotel-context';

/**
 * Hotel switcher built from the user's memberships. Switching navigates to the
 * target hotel's reception route; the route's slug-resolution + guard chain
 * then re-derives the scoped context (and therefore a fresh query-cache
 * partition — no manual cache clearing needed).
 *
 * Only hotel-scoped memberships (which carry a concrete slug) are offered.
 * Tenant-wide memberships need a hotel-list lookup to enumerate hotels — a
 * documented backend gap.
 */
export function HotelSwitcher() {
  const { t } = useT('platform');
  const { memberships } = useAuth();
  const hotel = useHotelContext();
  const navigate = useNavigate();

  const options = useMemo(
    () =>
      memberships
        .filter((m) => m.hotelId && m.hotelSlug && m.tenantSlug)
        .map((m) => ({
          value: `${m.tenantSlug}/${m.hotelSlug}`,
          label: m.hotelName ?? m.hotelSlug ?? m.hotelId ?? '',
        })),
    [memberships],
  );

  const current = `${hotel.tenantSlug}/${hotel.hotelSlug}`;

  if (options.length <= 1) {
    return (
      <span
        data-testid="hotel-switcher-static"
        className="font-[var(--font-display)] text-sm font-medium text-[var(--color-charcoal)]"
      >
        {hotel.hotelName}
      </span>
    );
  }

  return (
    <Select
      value={current}
      onValueChange={(value) => {
        const [tenantSlug, hotelSlug] = value.split('/');
        if (tenantSlug && hotelSlug) {
          void navigate({
            to: '/$tenantSlug/$hotelSlug/reception',
            params: { tenantSlug, hotelSlug },
          });
        }
      }}
    >
      <SelectTrigger data-testid="hotel-switcher-trigger" aria-label={t('shell.hotelSwitcher')}>
        <SelectValue placeholder={t('shell.hotelSwitcher')} />
      </SelectTrigger>
      <SelectContent data-testid="hotel-switcher-content">
        {options.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            data-testid={`hotel-switcher-option-${opt.value}`}
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
